export interface VimeoUrlInfo {
  isValid: boolean;
  videoId: string | null;
  originalUrl: string;
  normalizedUrl: string | null;
}

export interface VimeoOEmbedResponse {
  type: string;
  version: string;
  provider_name: string;
  provider_url: string;
  title: string;
  author_name: string;
  author_url: string;
  is_plus: string;
  account_type: string;
  html: string;
  width: number;
  height: number;
  duration: number;
  description: string;
  thumbnail_url: string;
  thumbnail_width: number;
  thumbnail_height: number;
  thumbnail_url_with_play_button: string;
  upload_date: string;
  video_id: number;
}

export interface VimeoThumbnailResult {
  success: boolean;
  thumbnailUrl: string | null;
  error?: string;
}

/**
 * Comprehensive regex pattern for Vimeo URL validation
 * Supports all common Vimeo URL formats:
 * - vimeo.com/VIDEO_ID
 * - player.vimeo.com/video/VIDEO_ID
 * - vimeo.com/channels/CHANNEL/VIDEO_ID
 * - vimeo.com/groups/GROUP/videos/VIDEO_ID
 * - vimeo.com/ondemand/TITLE/VIDEO_ID
 * - vimeo.com/showcase/SHOWCASE_ID/video/VIDEO_ID
 */
const VIMEO_URL_REGEX = /^(?:https?:\/\/)?(?:www\.|player\.)?vimeo\.com\/(?:channels\/[^/]+\/|groups\/[^/]+\/videos\/|ondemand\/[^/]+\/|showcase\/[^/]+\/video\/|video\/)?(\d+)(?:\?.*)?$/;

const VIMEO_URL_REGEX_EXTENDED = /^.*(?:vimeo\.com\/(?:channels\/[^/]+\/|groups\/[^/]+\/videos\/|ondemand\/[^/]+\/|showcase\/[^/]+\/video\/|video\/)?(\d+))(?:\?.*)?$/;

// Cache for oEmbed responses to avoid repeated API calls
const oEmbedCache = new Map<string, { data: VimeoOEmbedResponse; timestamp: number }>();
const CACHE_DURATION = 60 * 60 * 1000; // 1 hour in milliseconds

/**
 * Validates if a URL is a valid Vimeo URL
 * @param url - The URL to validate
 * @returns boolean indicating if the URL is a valid Vimeo URL
 */
export const isVimeoUrl = (url: string): boolean => {
  if (!url || typeof url !== 'string') {
    return false;
  }

  const trimmedUrl = url.trim();

  if (VIMEO_URL_REGEX.test(trimmedUrl)) {
    return true;
  }

  if (VIMEO_URL_REGEX_EXTENDED.test(trimmedUrl)) {
    return true;
  }

  return false;
};

/**
 * Extracts video ID from a Vimeo URL
 * @param url - The Vimeo URL
 * @returns The video ID or null if not found
 */
export const extractVimeoVideoId = (url: string): string | null => {
  if (!url || typeof url !== 'string') {
    return null;
  }

  const trimmedUrl = url.trim();

  const primaryMatch = trimmedUrl.match(VIMEO_URL_REGEX);
  if (primaryMatch && primaryMatch[1]) {
    return primaryMatch[1];
  }

  const extendedMatch = trimmedUrl.match(VIMEO_URL_REGEX_EXTENDED);
  if (extendedMatch && extendedMatch[1]) {
    return extendedMatch[1];
  }

  return null;
};

/**
 * Validates and parses a Vimeo URL, returning comprehensive information
 * @param url - The URL to validate and parse
 * @returns VimeoUrlInfo object with validation and parsing results
 */
export const validateVimeoUrl = (url: string): VimeoUrlInfo => {
  const result: VimeoUrlInfo = {
    isValid: false,
    videoId: null,
    originalUrl: url,
    normalizedUrl: null,
  };

  if (!url || typeof url !== 'string') {
    return result;
  }

  const trimmedUrl = url.trim();
  const videoId = extractVimeoVideoId(trimmedUrl);

  if (videoId) {
    result.isValid = true;
    result.videoId = videoId;
    result.normalizedUrl = `https://vimeo.com/${videoId}`;
  }

  return result;
};

/**
 * Fetches Vimeo video information using oEmbed API
 * @param url - The Vimeo URL
 * @returns Promise<VimeoOEmbedResponse | null>
 */
export const fetchVimeoOEmbedData = async (url: string): Promise<VimeoOEmbedResponse | null> => {
  try {
    // Check cache first
    const cacheKey = url;
    const cached = oEmbedCache.get(cacheKey);
    if (cached && (Date.now() - cached.timestamp) < CACHE_DURATION) {
      return cached.data;
    }

    const oEmbedUrl = `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(url)}`;
    const response = await fetch(oEmbedUrl);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data: VimeoOEmbedResponse = await response.json();

    // Cache the response
    oEmbedCache.set(cacheKey, { data, timestamp: Date.now() });

    return data;
  } catch (error) {
    console.error('Error fetching Vimeo oEmbed data:', error);
    return null;
  }
};

/**
 * Gets Vimeo video thumbnail URL using oEmbed API
 * @param url - The Vimeo URL
 * @returns Promise<VimeoThumbnailResult>
 */
export const getVimeoThumbnailUrl = async (url: string): Promise<VimeoThumbnailResult> => {
  try {
    const urlInfo = validateVimeoUrl(url);
    if (!urlInfo.isValid || !urlInfo.normalizedUrl) {
      return {
        success: false,
        thumbnailUrl: null,
        error: 'Invalid Vimeo URL',
      };
    }

    const oEmbedData = await fetchVimeoOEmbedData(urlInfo.normalizedUrl);
    if (!oEmbedData || !oEmbedData.thumbnail_url) {
      return {
        success: false,
        thumbnailUrl: null,
        error: 'Failed to fetch thumbnail from Vimeo API',
      };
    }

    return {
      success: true,
      thumbnailUrl: oEmbedData.thumbnail_url,
    };
  } catch (error) {
    return {
      success: false,
      thumbnailUrl: null,
      error: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
};

/**
 * Gets Vimeo video thumbnail URL by video ID
 * @param videoId - The Vimeo video ID
 * @returns Promise<VimeoThumbnailResult>
 */
export const getVimeoThumbnailUrlById = async (videoId: string): Promise<VimeoThumbnailResult> => {
  const url = `https://vimeo.com/${videoId}`;
  return getVimeoThumbnailUrl(url);
};

/**
 * Generates an embeddable Vimeo URL from a video ID
 * @param videoId - The Vimeo video ID
 * @param autoplay - Whether to enable autoplay
 * @param startTime - Start time in seconds
 * @returns The embeddable Vimeo URL
 */
export const getVimeoEmbedUrl = (
  videoId: string,
  autoplay: boolean = false,
  startTime?: number,
): string => {
  if (!videoId) {
    return '';
  }

  let embedUrl = `https://player.vimeo.com/video/${videoId}`;
  const params: string[] = [];

  if (autoplay) {
    params.push('autoplay=1');
  }

  if (startTime && startTime > 0) {
    params.push(`t=${startTime}s`);
  }

  if (params.length > 0) {
    embedUrl += `?${params.join('&')}`;
  }

  return embedUrl;
};

/**
 * Checks if a Vimeo video is accessible by attempting to fetch its oEmbed data
 * @param videoId - The Vimeo video ID
 * @returns Promise<boolean> indicating if the video is accessible
 */
export const checkVimeoVideoAccessibility = async (videoId: string): Promise<boolean> => {
  if (!videoId) {
    return false;
  }

  try {
    const url = `https://vimeo.com/${videoId}`;
    const oEmbedData = await fetchVimeoOEmbedData(url);
    return oEmbedData !== null;
  } catch {
    return false;
  }
};

/**
 * Clears the oEmbed cache
 */
export const clearVimeoCache = (): void => oEmbedCache.clear();

/**
 * Gets cached oEmbed data count (for debugging)
 */
export const getCacheSize = (): number => oEmbedCache.size;
